import { HistoryDetailsItem } from "@common/types/auditLog";
import { HistoryField } from "@common/constants/history-source-types";
import {
    HistoryAccountDetailsRow,
    HistoryAccountLine,
} from "../views/historyAccountDetailsViewConfig";
import {
    formatAccountFieldValue,
    IdentifierFormatter,
    readPreferredCoaSubject,
    readMergedIntoAccountId,
    readMergedIntoSubject,
} from "./coaActivityValues";
import {
    buildIdentityLine,
    buildRow,
    groupLogsBy,
    LogGroup,
} from "./historyAccountLines";

const TRANSFERRED_FIELD_LABELS: Partial<Record<string, string>> = {
    [HistoryField.ROLE]: "Role",
    [HistoryField.RELATED_ENTITY_ID]: "Related business",
};

const isMergeSourceLog = (log: HistoryDetailsItem): boolean =>
    readMergedIntoAccountId(log) !== undefined;

function getMergeActionKey(log: HistoryDetailsItem): string {
    if (log.sourceId) {
        return log.sourceId;
    }
    return isMergeSourceLog(log) ? log.resourceId : log.id;
}

function describeTransferredIdentity(
    targetLogs: HistoryDetailsItem[],
): string | null {
    const notes = targetLogs.flatMap((log) =>
        log.diff.flatMap((diff) => {
            const label = TRANSFERRED_FIELD_LABELS[diff.field];
            return label
                ? [
                      `${label}: ${formatAccountFieldValue(diff.field, diff.after)}`,
                  ]
                : [];
        }),
    );
    return notes.length > 0 ? notes.join(", ") : null;
}

interface BuildTargetLinesParams {
    sourceLogs: HistoryDetailsItem[];
    targetLogs: HistoryDetailsItem[];
    formatIdentifier: IdentifierFormatter;
}

function buildTargetLines({
    sourceLogs,
    targetLogs,
    formatIdentifier,
}: BuildTargetLinesParams): HistoryAccountLine[] {
    const [targetLog] = targetLogs;
    const [sourceLog] = sourceLogs;
    const subject =
        (targetLog && readPreferredCoaSubject(targetLog)) ??
        (sourceLog && readMergedIntoSubject(sourceLog));
    const log = targetLog ?? sourceLog;
    if (!subject || !log) {
        return [];
    }
    return [
        {
            ...buildIdentityLine({
                log,
                formatIdentifier,
                subject,
                label: "Merged to",
            }),
            id: `${log.id}-merged-to`,
            identifierNote: describeTransferredIdentity(targetLogs),
        },
    ];
}

interface BuildMergeRowParams {
    actionLogs: LogGroup;
    formatIdentifier: IdentifierFormatter;
}

function buildMergeRow({
    actionLogs,
    formatIdentifier,
}: BuildMergeRowParams): HistoryAccountDetailsRow {
    const sourceLogs = actionLogs.filter(isMergeSourceLog);
    const targetLogs = actionLogs.filter((log) => !isMergeSourceLog(log));
    const sourceLines = sourceLogs.map((log) =>
        buildIdentityLine({ log, formatIdentifier, label: "Merged" }),
    );
    const [firstLog] = actionLogs;
    return buildRow({
        id: firstLog.id,
        log: firstLog,
        lines: [
            ...sourceLines,
            ...buildTargetLines({ sourceLogs, targetLogs, formatIdentifier }),
        ],
    });
}

interface BuildMergeRowsParams {
    logs: HistoryDetailsItem[];
    formatIdentifier: IdentifierFormatter;
}

export function buildMergeRows({
    logs,
    formatIdentifier,
}: BuildMergeRowsParams): HistoryAccountDetailsRow[] {
    return groupLogsBy(logs, getMergeActionKey).map((actionLogs) =>
        buildMergeRow({ actionLogs, formatIdentifier }),
    );
}
